import { useCallback, useEffect, useState } from 'react';

import { ancanaApi } from '../../../api/ancanaApi';
import { ISeoMeta } from '../../../types/models/seo';

/**
 * Loads and saves the `seo.SeoMeta` row for one content record.
 *
 * The row is separate from the record being edited, so this manages its own fetch and
 * save rather than riding the host form. Two things make it fiddly enough to be worth
 * isolating here:
 *
 *  - Per-locale text lives in `LocalizedText`, not on the row. The API accepts
 *    `title__en` / `description__es` on write, but on read only returns the single
 *    locale matching `?lang=`. So all locales are loaded from `localized-texts/`, the
 *    same way `RecordDrawer` does it for other localized models.
 *  - `LocalizedText.field_name` is stored SINGULAR (`title`, not `titles`) — that is
 *    what the API's read path derives from the serializer method name.
 */

export interface LocalizedValues {
  /** `{ en: '…', es: '…' }` */
  [localeCode: string]: string;
}

export interface SeoMetaDraft {
  titles: LocalizedValues;
  descriptions: LocalizedValues;
  og_image: string;
  canonical_path: string;
  focus_keyword: string;
  noindex: boolean;
}

export const EMPTY_DRAFT: SeoMetaDraft = {
  titles: {},
  descriptions: {},
  og_image: '',
  canonical_path: '',
  focus_keyword: '',
  noindex: false,
};

// Cached for the session: neither the ContentType of SeoMeta nor the locale list
// changes, and every panel would otherwise refetch both.
let seoMetaContentTypeId: number | undefined;
let cachedLocaleCodes: string[] | undefined;

const loadSeoMetaContentTypeId = async (): Promise<number | undefined> => {
  if (seoMetaContentTypeId) return seoMetaContentTypeId;
  // OPTIONS on the endpoint reports the ContentType of the model it serves.
  const response = await ancanaApi.options('seo/meta/');
  seoMetaContentTypeId = response.data?.content_type?.id;
  return seoMetaContentTypeId;
};

const loadLocaleCodes = async (): Promise<string[]> => {
  if (cachedLocaleCodes) return cachedLocaleCodes;
  const response = await ancanaApi.get('locales/?page_size=50');
  const results = response.data?.results || response.data || [];
  cachedLocaleCodes = results.map((locale: any) => locale.code);
  return cachedLocaleCodes as string[];
};

export interface UseSeoMetaResult {
  isLoading: boolean;
  isSaving: boolean;
  /** The persisted row, or undefined when this record has no overrides yet. */
  seoMeta?: ISeoMeta;
  draft: SeoMetaDraft;
  localeCodes: string[];
  setDraft: (updater: (previous: SeoMetaDraft) => SeoMetaDraft) => void;
  isDirty: boolean;
  save: () => Promise<void>;
  reload: () => Promise<void>;
  error?: string;
}

export const useSeoMeta = (
  contentTypeId?: number,
  objectId?: number | string,
): UseSeoMetaResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [seoMeta, setSeoMeta] = useState<ISeoMeta | undefined>();
  const [draft, setDraftState] = useState<SeoMetaDraft>(EMPTY_DRAFT);
  const [savedDraft, setSavedDraft] = useState<SeoMetaDraft>(EMPTY_DRAFT);
  const [localeCodes, setLocaleCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    if (!contentTypeId || objectId === undefined) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const [codes, response] = await Promise.all([
        loadLocaleCodes(),
        ancanaApi.get(`seo/meta/?content_type=${contentTypeId}&object_id=${objectId}`),
      ]);
      setLocaleCodes(codes);

      const row: ISeoMeta | undefined = (response.data?.results || [])[0];
      setSeoMeta(row);

      if (!row) {
        setDraftState(EMPTY_DRAFT);
        setSavedDraft(EMPTY_DRAFT);
        return;
      }

      const ownContentTypeId = await loadSeoMetaContentTypeId();
      const titles: LocalizedValues = {};
      const descriptions: LocalizedValues = {};

      if (ownContentTypeId) {
        const texts = await ancanaApi.get(
          `localized-texts/?content_type=${ownContentTypeId}&object_id=${row.id}&page_size=50`,
        );
        (texts.data?.results || []).forEach((text: any) => {
          if (text.field_name === 'title') titles[text.locale] = text.text;
          if (text.field_name === 'description') descriptions[text.locale] = text.text;
        });
      }

      const loaded: SeoMetaDraft = {
        titles,
        descriptions,
        og_image: row.og_image || '',
        canonical_path: row.canonical_path || '',
        focus_keyword: row.focus_keyword || '',
        noindex: !!row.noindex,
      };
      setDraftState(loaded);
      setSavedDraft(loaded);
    } catch (e: any) {
      setError(e?.message || 'Could not load SEO metadata.');
    } finally {
      setIsLoading(false);
    }
  }, [contentTypeId, objectId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    if (!contentTypeId || objectId === undefined) return;
    setIsSaving(true);
    setError(undefined);
    try {
      // Locale suffixes are what the API's LocalizedWriteCharField expects.
      const payload: any = {
        og_image: draft.og_image || null,
        canonical_path: draft.canonical_path,
        focus_keyword: draft.focus_keyword,
        noindex: draft.noindex,
      };
      localeCodes.forEach(code => {
        payload[`title__${code}`] = draft.titles[code] || '';
        payload[`description__${code}`] = draft.descriptions[code] || '';
      });

      let saved: ISeoMeta;
      if (seoMeta) {
        saved = (await ancanaApi.patch(`seo/meta/${seoMeta.id}/`, payload)).data;
      } else {
        // First save for this record creates the row and binds it to the target.
        saved = (
          await ancanaApi.post('seo/meta/', {
            ...payload,
            content_type: contentTypeId,
            object_id: objectId,
          })
        ).data;
      }
      setSeoMeta(saved);
      setSavedDraft(draft);
    } catch (e: any) {
      const detail = e?.response?.data;
      setError(
        typeof detail === 'string'
          ? detail
          : JSON.stringify(detail || e?.message || 'Save failed.'),
      );
      throw e;
    } finally {
      setIsSaving(false);
    }
  }, [contentTypeId, objectId, draft, localeCodes, seoMeta]);

  const setDraft = useCallback(
    (updater: (previous: SeoMetaDraft) => SeoMetaDraft) => setDraftState(updater),
    [],
  );

  return {
    isLoading,
    isSaving,
    seoMeta,
    draft,
    localeCodes,
    setDraft,
    isDirty: JSON.stringify(draft) !== JSON.stringify(savedDraft),
    save,
    reload: load,
    error,
  };
};
